import React, { useState, useEffect, useRef } from "react";
import { Spinner, Button } from 'react-bootstrap';
import config from '../configuration/config.json';
import axios from 'axios';

// Live face verification against the MegaMatcher ID (Neurotech) server over Janus/WebRTC.
// All MegaMatcher calls go through the Liveness API, which holds the server credentials and
// pins the operation to "Verify against the enrolled DG2/portrait" for this application.

const STATUS_MESSAGES = {
    0: "Position your face inside the frame",
    1: "Verification completed",
    2: "Verification timed out",
    3: "Verification cancelled",
    5: "Face does not match the document",
    7: "Face not found",
    8: "Liveness check failed",
    9: "Hold the camera steady, the image is not sharp",
    10: "Image is too noisy, move to a better lit place",
    11: "Bad lighting, move to a better lit place",
    12: "Do not cover your face",
    13: "Look straight at the camera",
    14: "Only one face should be visible",
    15: "Please remove your mask",
    18: "Hold still, the image is blurred",
    20: "Move closer to the camera",
    21: "Move back from the camera",
    22: "Verification failed",
    998: "Adjusting image quality...",
    999: "Internal error"
};

const LIVENESS_ACTIONS = [
    [1, "Keep still"],
    [2, "Blink"],
    [4, "Turn your head slowly"],
    [8, "Keep turning your head from side to side"],
    [16, "Look at the center"],
    [32, "Turn left"],
    [64, "Turn right"],
    [128, "Look up"],
    [256, "Look down"],
    [512, "Move closer"],
    [1024, "Move back"]
];

function describeActions(livenessAction) {
    let flags = Array.isArray(livenessAction)
        ? livenessAction.reduce((acc, a) => acc | a, 0)
        : (livenessAction || 0);
    return LIVENESS_ACTIONS.filter(([bit]) => (flags & bit) !== 0).map(([, text]) => text).join(", ");
}

async function captureFrame(video) {
    if (video.readyState < 2) {
        await new Promise(resolve => video.addEventListener('loadeddata', resolve, { once: true }));
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.9).replace(/^data:image\/[a-z]+;base64,/, "");
}

function parseBody(data) {
    return typeof data === 'string' && data !== '' ? JSON.parse(data) : data;
}

function NeurotechVerify({ aid, cardType, onSuccess, onError }) {
    const [isRunning, setIsRunning] = useState(false);
    const [instruction, setInstruction] = useState("");
    const videoRef = useRef(null);
    const sessionRef = useRef(null);
    const pcRef = useRef(null);
    const streamRef = useRef(null);
    const selfieRef = useRef(null);

    const baseUrl = config.idproofing.apibaseurl + "idproofing/neurotech/";
    const headers = {
        "content-type": "application/json",
        'Authorization': "Bearer " + config.idproofing.token
    };

    async function post(path, data) {
        const response = await axios.request({ method: 'POST', url: baseUrl + path, headers: headers, data: data });
        return parseBody(response.data);
    }

    function cleanup() {
        if (pcRef.current) {
            pcRef.current.close();
            pcRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (sessionRef.current) {
            post("janus/stop", sessionRef.current).catch(() => { });
            sessionRef.current = null;
        }
    }

    useEffect(() => cleanup, []);

    function fail(e) {
        cleanup();
        setIsRunning(false);
        let message = e.message;
        if (e.response != null && e.response.data != null) {
            message = parseBody(e.response.data).message;
        }
        onError(message);
    }

    async function finish() {
        cleanup();
        try {
            // The selfie is sent along because the eID issuance (OCR API) uses the stored selfie image.
            const result = await post("result/" + aid, { image: selfieRef.current, imageType: "jpeg" });
            setIsRunning(false);
            if (result.status === 'Success') {
                onSuccess();
            } else {
                onError(result.message);
            }
        } catch (e) {
            fail(e);
        }
    }

    async function start() {
        setIsRunning(true);
        setInstruction("Starting camera...");
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
                audio: false
            });
            streamRef.current = stream;
            videoRef.current.srcObject = stream;

            setInstruction("Preparing verification...");
            selfieRef.current = await captureFrame(videoRef.current);

            // The API enrolls this application's DG2/portrait as the subject to verify against.
            const session = await post("janus/create-and-attach/" + aid, { cardType: cardType });
            sessionRef.current = { id: session.id, handleId: session.handleId };

            const pc = new RTCPeerConnection({ iceServers: config.idproofing.neurotech?.iceServers || [] });
            pcRef.current = pc;
            stream.getTracks().forEach(track => pc.addTrack(track, stream));

            // Candidates found before connect completes are held back; Janus needs the offer first.
            let connected = false;
            const pendingCandidates = [];
            const trickle = (candidate) => post("janus/trickle", {
                session: sessionRef.current,
                data: { candidate: candidate.candidate, sdpMid: candidate.sdpMid, sdpMLineIndex: candidate.sdpMLineIndex }
            }).catch(() => { });
            pc.onicecandidate = (event) => {
                if (!event.candidate) return;
                if (connected) trickle(event.candidate);
                else pendingCandidates.push(event.candidate);
            };

            // Channel label and options as in the MegaMatcherIdWebClientSample; each message is one frame result.
            const dataChannel = pc.createDataChannel("mmidJanus", { maxPacketLifeTime: 3000 });
            dataChannel.onmessage = (event) => {
                let frame = parseBody(event.data);
                if (!frame) return;

                if (frame.operationFinished) {
                    setInstruction(STATUS_MESSAGES[frame.status] || "Verification finished");
                    finish();
                    return;
                }
                let actions = describeActions(frame.livenessAction);
                setInstruction(actions || STATUS_MESSAGES[frame.status] || "Hold still...");
            };

            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);

            const connectResponse = await post("janus/connect/" + aid, {
                jsep: { type: offer.type, sdp: offer.sdp },
                session: sessionRef.current
            });
            if (connectResponse.plugindata?.data?.error || connectResponse.jsep == null) {
                throw new Error(connectResponse.plugindata?.data?.error || "Unable to start face verification");
            }
            await pc.setRemoteDescription(connectResponse.jsep);

            connected = true;
            pendingCandidates.forEach(trickle);
            setInstruction("Position your face inside the frame");
        } catch (e) {
            fail(e);
        }
    }

    return (
        <>
            <div className="row">
                <div className="col">
                    <video ref={videoRef} autoPlay playsInline muted className='m-4'
                        style={{ width: 320, height: 300, objectFit: 'cover', transform: 'scaleX(-1)', display: isRunning ? 'inline' : 'none' }} />
                </div>
            </div>
            {isRunning && (
                <div className="row">
                    <div className="col">
                        <span className="scaninstructiondisplay">{instruction}</span>
                    </div>
                </div>
            )}
            <div className="row">
                <div className="col">
                    <Button className="btn btn-primary btn-lg px-4 gap-3" type="button" disabled={isRunning} onClick={start}>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" hidden={!isRunning} />
                        <span className="px-2">Start Face Verification</span>
                    </Button>
                </div>
            </div>
        </>
    );
}

export default NeurotechVerify;
